import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Search, X } from 'lucide-react';
import type { Account } from '@/lib/api/helpdesk';
import { rankAccounts } from '@/lib/helpdesk/matchAccounts';

interface Props {
  customerName: string;
  accounts: Account[];
  /** account_id → the customer already holding it, so a taken account explains itself. */
  linkedBy: Record<string, string>;
  onPick: (account: Account) => void;
  onClose: () => void;
}

function matches(account: Account, term: string): boolean {
  return !term || account.name.toLowerCase().includes(term) || account.id.toLowerCase().includes(term);
}

/** Centred account picker. A dialog rather than an inline panel: the customer list scrolls. */
export function AccountPickerModal({ customerName, accounts, linkedBy, onPick, onClose }: Props) {
  const [search, setSearch] = useState('');
  const searchBox = useRef<HTMLInputElement>(null);

  useEffect(() => {
    searchBox.current?.focus();
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const term = search.trim().toLowerCase();
  const [available, taken] = useMemo(() => {
    const shown = rankAccounts(customerName, accounts.filter(account => matches(account, term)));
    return [
      shown.filter(account => !linkedBy[account.id]),
      shown.filter(account => linkedBy[account.id]),
    ];
  }, [accounts, customerName, linkedBy, term]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
        role="dialog" aria-modal="true" aria-label={`Select an account for ${customerName}`}
        className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] flex flex-col"
        onClick={event => event.stopPropagation()}>
        <div className="p-5 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Select an account</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">for {customerName}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 dark:text-gray-400">
            <X className="w-5 h-5" aria-hidden />
          </button>
        </div>

        <div className="p-5 pb-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden />
            <input ref={searchBox} type="search" value={search} onChange={event => setSearch(event.target.value)}
              placeholder="Search accounts…" aria-label={`Search accounts for ${customerName}`}
              className="w-full pl-9 pr-3 py-2 bg-white dark:bg-slate-900 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
        </div>

        <div className="px-5 pb-5 overflow-y-auto">
          {available.length === 0 && taken.length === 0 && (
            <p className="py-6 text-sm text-center text-gray-500 dark:text-gray-400">
              {accounts.length === 0
                ? 'No accounts are available yet. Reconnect the integration to load them.'
                : 'No accounts match your search.'}
            </p>
          )}

          {available.length > 0 && (
            <>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {term ? 'Matches' : 'Closest to this customer first'}
            </p>
            <ul className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700">
              {available.map(account => (
                <li key={account.id}>
                  <button type="button" onClick={() => onPick(account)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-slate-700">
                    <div className="text-sm text-gray-900 dark:text-white">{account.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      ID: {account.id}{account.website ? ` • ${account.website}` : ''}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
            </>
          )}

          {taken.length > 0 && (
            <>
              <p className="mt-4 mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Already linked
              </p>
              <ul className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700">
                {taken.map(account => (
                  <li key={account.id} className="px-3 py-2 opacity-60">
                    <div className="text-sm text-gray-900 dark:text-white">{account.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Linked to {linkedBy[account.id]} — unlink it there to reuse it
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
